import type { StockItem } from '../types/stock'
import { getStockShortfall, isStockItemLow, type StockLowCheckInput } from './stockLevels'

export { getStockShortfall, isStockItemLow, type StockLowCheckInput }

// ============================================================================
// Overview Insights — Pure Derivation Functions (R29 O1.b)
// ============================================================================

const MS_PER_DAY = 24 * 60 * 60 * 1000

export type ViewerTier = 'lead' | 'viewer' | 'helper'

export interface InsightChecklistItem {
  id: string
  title?: string
  text?: string
  assigneeId?: string | null
  assigneeName?: string | null
  isCompleted?: boolean
  workflowStatus?: string | null
  boardStatus?: string | null
  completedAt?: unknown
  updatedAt?: unknown
  dueAt?: string | number | Date | null
  scheduleEndDate?: string | number | Date | null
  endDate?: string | number | Date | null
  occurrences?: Record<
    string,
    {
      isCompleted?: boolean
      workflowStatus?: string | null
      completedAt?: string | null
    }
  >
  scopeRead?: string[]
  scopeWrite?: string[]
  assignedEditors?: string[]
  createdById?: string | null
  isArchived?: boolean
  deletedAt?: unknown
}

export interface InsightMember {
  uid: string
  displayName?: string | null
  role?: string | null
  lastActiveAt?: unknown
}

export interface InsightGoal {
  id: string
  title: string
  progress?: number | null
  startDate?: string | number | Date | null
  endDate?: string | number | Date | null
  boardStatus?: string | null
  workflowStatus?: string | null
  checklist?: InsightChecklistItem[]
  tasks?: Array<{
    id?: string
    progress?: number | null
    boardStatus?: string | null
    workflowStatus?: string | null
    checklist?: InsightChecklistItem[]
    subTasks?: Array<{
      id?: string
      progress?: number | null
      boardStatus?: string | null
      workflowStatus?: string | null
      checklist?: InsightChecklistItem[]
    }>
  }>
}

export interface InsightElement {
  id: string
  title: string
  type?: 'GOAL' | 'TASK' | 'SUBTASK' | 'MILESTONE' | string
  startDate?: string | number | Date | null
  endDate?: string | number | Date | null
  isCompleted?: boolean
  boardStatus?: string | null
  workflowStatus?: string | null
  progress?: number | null
}

export interface PersonStats {
  uid: string
  open: number
  done7d: number
  overdue: number
  completedTotal: number
  totalAssigned: number
  lastActiveAt: number | null
}

export interface TeamPersonRow extends PersonStats {
  displayName: string
  role?: string | null
  flag: 'overloaded' | 'quiet' | null
}

export interface TeamStatsResult {
  open: number
  done7d: number
  overdue: number
  unassignedOpen: number
  activePeopleCount: number
  medianOpen: number
  overloadedThreshold: number
  people: TeamPersonRow[]
}

export type GoalTrackStatus = 'done' | 'on_track' | 'at_risk' | 'off_track'

export interface GoalProgressRow {
  id: string
  title: string
  progress: number
  startDateMs: number | null
  endDateMs: number | null
  elapsedShare: number | null
  daysRemaining: number | null
  isPastDue: boolean
  status: GoalTrackStatus
}

export interface GoalProgressSummary {
  total: number
  completed: number
  onTrack: number
  atRisk: number
  offTrack: number
  averageProgress: number
  goals: GoalProgressRow[]
}

export interface UpcomingDateRow {
  id: string
  title: string
  type: string
  endDateMs: number
  daysUntil: number
}

export type HealthState = 'on_track' | 'at_risk' | 'off_track'

export type HealthLabel = 'On track' | 'At risk' | 'Off track'

export type HealthCauseKind =
  | 'goal_past_due'
  | 'overdue_over_15_pct'
  | 'goal_behind_schedule'
  | 'overdue_over_5_pct'
  | 'healthy'

export interface HealthInput {
  goals?: InsightGoal[] | GoalProgressRow[]
  items?: InsightChecklistItem[]
  openCount?: number
  overdueCount?: number
  now?: Date | number | string
}

export interface HealthResult {
  state: HealthState
  label: HealthLabel
  causeKind: HealthCauseKind
  worstCause: string | null
}

export type AttentionItemKind =
  | 'person_overloaded'
  | 'person_quiet'
  | 'item_overdue'
  | 'stock_low'
  | 'budget_pace'
  | 'goal_off_track'

export type AttentionSeverity = 'bad' | 'warn'

export interface BudgetPaceInput {
  usedMinor?: number
  limitMinor?: number
  usedShare?: number
  elapsedTimeShare?: number
  startDate?: string | number | Date | null
  endDate?: string | number | Date | null
}

export interface AttentionItem {
  id: string
  kind: AttentionItemKind
  severity: AttentionSeverity
  title: string
  detail: string
  targetId?: string
  assigneeId?: string | null
  scopeWrite?: string[]
  assignedEditors?: string[]
  createdById?: string | null
  canAct?: boolean
}

export interface AttentionInput {
  items?: InsightChecklistItem[]
  members?: InsightMember[]
  goals?: InsightGoal[]
  stockItems?: Array<StockLowCheckInput & { id?: string; name?: string; unit?: string }> | StockItem[]
  budget?: BudgetPaceInput | null
  viewerTier?: ViewerTier
  viewerUid?: string | null
  viewerRole?: string | null
  isOwner?: boolean
  now?: Date | number | string
}

export interface ViewerContext {
  uid?: string | null
  role?: string | null
  isOwner?: boolean
  tier?: ViewerTier
  editableIds?: ReadonlySet<string> | string[]
}

export interface OverviewTierData {
  you?: PersonStats | null
  teamStats?: TeamStatsResult | null
  people?: TeamPersonRow[]
  attention?: AttentionItem[]
  goals?: GoalProgressSummary | GoalProgressRow[] | null
  upcoming?: UpcomingDateRow[]
  activityFeed?: unknown[] | null
  modules?: Record<string, unknown> | null
}

// ----------------------------------------------------------------------------
// Internal Date & Item Helpers
// ----------------------------------------------------------------------------

const toTimestampMs = (raw: unknown): number | null => {
  if (raw == null) return null
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null
  }
  if (raw instanceof Date) {
    const ms = raw.getTime()
    return Number.isFinite(ms) ? ms : null
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const parsed = Date.parse(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  if (typeof raw === 'object') {
    const maybeToMillis = (raw as { toMillis?: unknown }).toMillis
    if (typeof maybeToMillis === 'function') {
      const ms = (maybeToMillis as () => number).call(raw)
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null
    }
    const maybeSeconds = (raw as { seconds?: unknown }).seconds
    if (typeof maybeSeconds === 'number' && Number.isFinite(maybeSeconds)) {
      return maybeSeconds * 1000
    }
  }
  return null
}

const resolveNowMs = (now?: Date | number | string): number => {
  const ms = toTimestampMs(now)
  return ms ?? Date.now()
}

/**
 * Interprets a date-only string ("YYYY-MM-DD") at end-of-day UTC (23:59:59.999)
 * when checking if a due date has passed, or parses full ISO strings directly.
 */
const toDueEndMs = (raw: unknown): number | null => {
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const parsed = Date.parse(`${trimmed}T23:59:59.999Z`)
      return Number.isFinite(parsed) ? parsed : null
    }
  }
  return toTimestampMs(raw)
}

const isItemCompleted = (item: InsightChecklistItem): boolean => {
  if (item.isCompleted === true) return true
  if (item.workflowStatus === 'COMPLETED' || item.workflowStatus === 'NOT_REQUIRED') return true
  if (item.boardStatus === 'COMPLETE') return true
  return false
}

const isItemIgnored = (item: InsightChecklistItem): boolean => {
  if (!item || typeof item !== 'object') return true
  if (item.isArchived === true) return true
  if (item.deletedAt != null) return true
  return false
}

const getItemCompletedAtMs = (item: InsightChecklistItem): number | null => {
  const direct = toTimestampMs(item.completedAt)
  if (direct != null) return direct
  if (item.occurrences && typeof item.occurrences === 'object') {
    let latest: number | null = null
    for (const occ of Object.values(item.occurrences)) {
      if (occ?.isCompleted && occ.completedAt) {
        const ms = toTimestampMs(occ.completedAt)
        if (ms != null && (latest == null || ms > latest)) {
          latest = ms
        }
      }
    }
    if (latest != null) return latest
  }
  return toTimestampMs(item.updatedAt)
}

const getItemDueMs = (item: InsightChecklistItem): number | null => {
  return toDueEndMs(item.dueAt ?? item.scheduleEndDate ?? item.endDate)
}

const isItemOverdue = (item: InsightChecklistItem, nowMs: number): boolean => {
  if (isItemCompleted(item)) return false
  const dueMs = getItemDueMs(item)
  if (dueMs == null) return false
  return dueMs < nowMs
}

const computeMedian = (values: number[]): number => {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) {
    return sorted[mid]
  }
  return (sorted[mid - 1] + sorted[mid]) / 2
}

// ----------------------------------------------------------------------------
// C1. personStats, teamStats, goalProgress, upcomingDates
// ----------------------------------------------------------------------------

/**
 * Computes `{ open, done7d, overdue }` for a specific user from assigned checklist items.
 * Uncapped (unlike `buildAssigneeLoad` which slices to 8).
 */
export const personStats = (
  items: InsightChecklistItem[],
  uid: string,
  now: Date | number | string = Date.now(),
): PersonStats => {
  const nowMs = resolveNowMs(now)
  const sevenDaysAgoMs = nowMs - 7 * MS_PER_DAY
  const normalizedUid = uid.trim()

  let open = 0
  let done7d = 0
  let overdue = 0
  let completedTotal = 0
  let totalAssigned = 0
  let lastActiveAt: number | null = null

  if (!normalizedUid) {
    return { uid: '', open: 0, done7d: 0, overdue: 0, completedTotal: 0, totalAssigned: 0, lastActiveAt: null }
  }

  for (const item of items) {
    if (isItemIgnored(item)) continue
    if (item.assigneeId !== normalizedUid) continue

    totalAssigned += 1
    const completed = isItemCompleted(item)
    const completedAtMs = getItemCompletedAtMs(item)
    const updatedAtMs = toTimestampMs(item.updatedAt) ?? completedAtMs
    if (updatedAtMs != null && (lastActiveAt == null || updatedAtMs > lastActiveAt)) {
      lastActiveAt = updatedAtMs
    }

    if (completed) {
      completedTotal += 1
      if (completedAtMs != null && completedAtMs >= sevenDaysAgoMs && completedAtMs <= nowMs + MS_PER_DAY) {
        done7d += 1
      }
    } else {
      open += 1
      if (isItemOverdue(item, nowMs)) {
        overdue += 1
      }
    }
  }

  return {
    uid: normalizedUid,
    open,
    done7d,
    overdue,
    completedTotal,
    totalAssigned,
    lastActiveAt,
  }
}

/**
 * Aggregates uncapped per-person and team-wide checklist stats, including
 * the overloaded threshold (`Math.max(6, 2 * medianOpen)`) and quiet flag (`5+` days inactive with open > 0).
 */
export const teamStats = (
  items: InsightChecklistItem[],
  members: InsightMember[] = [],
  now: Date | number | string = Date.now(),
): TeamStatsResult => {
  const nowMs = resolveNowMs(now)
  const sevenDaysAgoMs = nowMs - 7 * MS_PER_DAY
  const fiveDaysAgoMs = nowMs - 5 * MS_PER_DAY

  let open = 0
  let done7d = 0
  let overdue = 0
  let unassignedOpen = 0

  const memberMap = new Map<string, InsightMember>()
  for (const m of members) {
    if (m?.uid) {
      memberMap.set(m.uid, m)
    }
  }

  const uidSet = new Set<string>(memberMap.keys())
  for (const item of items) {
    if (isItemIgnored(item)) continue
    const completed = isItemCompleted(item)
    const completedAtMs = getItemCompletedAtMs(item)

    if (completed) {
      if (completedAtMs != null && completedAtMs >= sevenDaysAgoMs && completedAtMs <= nowMs + MS_PER_DAY) {
        done7d += 1
      }
    } else {
      open += 1
      if (isItemOverdue(item, nowMs)) {
        overdue += 1
      }
      if (!item.assigneeId) {
        unassignedOpen += 1
      }
    }

    if (item.assigneeId) {
      uidSet.add(item.assigneeId)
    }
  }

  const rawPeople: Array<PersonStats & { displayName: string; role?: string | null }> = []
  for (const uid of uidSet) {
    const stats = personStats(items, uid, nowMs)
    const member = memberMap.get(uid)
    const memberLastActiveMs = toTimestampMs(member?.lastActiveAt)
    const effectiveLastActive =
      memberLastActiveMs != null && (stats.lastActiveAt == null || memberLastActiveMs > stats.lastActiveAt)
        ? memberLastActiveMs
        : stats.lastActiveAt

    rawPeople.push({
      ...stats,
      lastActiveAt: effectiveLastActive,
      displayName: member?.displayName?.trim() || `Member ${uid.slice(0, 6)}`,
      role: member?.role ?? null,
    })
  }

  // Median open across assignees who have at least 1 assigned item (or all members if provided)
  const activeAssignees = rawPeople.filter((p) => p.totalAssigned > 0 || memberMap.has(p.uid))
  const openCounts = activeAssignees.map((p) => p.open)
  const medianOpen = computeMedian(openCounts)
  // OV-P5: Overloaded when open > 2x team median, minimum 6.
  // i.e. open >= 6 && open > 2 * medianOpen
  const overloadedThreshold = Math.max(6, Math.floor(2 * medianOpen) + 1)

  const people: TeamPersonRow[] = rawPeople
    .map((p) => {
      const isOverloaded = p.open >= 6 && p.open > 2 * medianOpen
      const isQuiet =
        !isOverloaded &&
        p.open > 0 &&
        p.lastActiveAt != null &&
        nowMs - p.lastActiveAt >= 5 * MS_PER_DAY &&
        p.lastActiveAt <= fiveDaysAgoMs
      return {
        ...p,
        flag: isOverloaded ? ('overloaded' as const) : isQuiet ? ('quiet' as const) : null,
      }
    })
    .sort((a, b) => {
      if (b.overdue !== a.overdue) return b.overdue - a.overdue
      if (b.open !== a.open) return b.open - a.open
      return a.displayName.localeCompare(b.displayName)
    })

  const activePeopleCount = people.filter((p) => p.open > 0 || p.done7d > 0).length

  return {
    open,
    done7d,
    overdue,
    unassignedOpen,
    activePeopleCount,
    medianOpen,
    overloadedThreshold,
    people,
  }
}

const deriveGoalProgressPercent = (goal: InsightGoal): number => {
  if (typeof goal.progress === 'number' && Number.isFinite(goal.progress)) {
    return Math.max(0, Math.min(100, Math.round(goal.progress)))
  }
  if (goal.boardStatus === 'COMPLETE' || goal.workflowStatus === 'COMPLETE') {
    return 100
  }

  const allChecklist: InsightChecklistItem[] = []
  if (Array.isArray(goal.checklist)) {
    allChecklist.push(...goal.checklist)
  }
  if (Array.isArray(goal.tasks)) {
    for (const t of goal.tasks) {
      if (Array.isArray(t.checklist)) allChecklist.push(...t.checklist)
      if (Array.isArray(t.subTasks)) {
        for (const st of t.subTasks) {
          if (Array.isArray(st.checklist)) allChecklist.push(...st.checklist)
        }
      }
    }
  }
  const activeChecklist = allChecklist.filter((c) => !isItemIgnored(c))
  if (activeChecklist.length > 0) {
    const done = activeChecklist.filter((c) => isItemCompleted(c)).length
    return Math.round((done / activeChecklist.length) * 100)
  }

  if (Array.isArray(goal.tasks) && goal.tasks.length > 0) {
    const doneTasks = goal.tasks.filter(
      (t) => t.boardStatus === 'COMPLETE' || t.workflowStatus === 'COMPLETE' || t.progress === 100,
    ).length
    return Math.round((doneTasks / goal.tasks.length) * 100)
  }

  return 0
}

/**
 * Computes goal progress and track status (`done`, `on_track`, `at_risk`, `off_track`)
 * per OV-P4 / §3.5.
 */
export const goalProgress = (
  goals: InsightGoal[],
  now: Date | number | string = Date.now(),
): GoalProgressSummary => {
  const nowMs = resolveNowMs(now)
  const rows: GoalProgressRow[] = []

  for (const goal of goals) {
    if (!goal || typeof goal !== 'object') continue
    const progress = deriveGoalProgressPercent(goal)
    const startDateMs = toTimestampMs(goal.startDate)
    const endDateMs = toDueEndMs(goal.endDate)

    let elapsedShare: number | null = null
    if (startDateMs != null && endDateMs != null && endDateMs > startDateMs) {
      const rawShare = ((nowMs - startDateMs) / (endDateMs - startDateMs)) * 100
      elapsedShare = Math.max(0, Math.min(100, rawShare))
    }

    const daysRemaining = endDateMs != null ? (endDateMs - nowMs) / MS_PER_DAY : null
    const isPastDue = endDateMs != null && endDateMs < nowMs && progress < 100

    let status: GoalTrackStatus = 'on_track'
    if (progress >= 100) {
      status = 'done'
    } else if (isPastDue) {
      status = 'off_track'
    } else if (
      daysRemaining != null &&
      daysRemaining >= 0 &&
      daysRemaining <= 14 &&
      elapsedShare != null &&
      elapsedShare - progress > 20
    ) {
      status = 'at_risk'
    }

    rows.push({
      id: goal.id,
      title: goal.title,
      progress,
      startDateMs,
      endDateMs,
      elapsedShare,
      daysRemaining,
      isPastDue,
      status,
    })
  }

  const total = rows.length
  const completed = rows.filter((r) => r.status === 'done').length
  const onTrack = rows.filter((r) => r.status === 'on_track').length
  const atRisk = rows.filter((r) => r.status === 'at_risk').length
  const offTrack = rows.filter((r) => r.status === 'off_track').length
  const averageProgress =
    total > 0 ? Math.round(rows.reduce((acc, r) => acc + r.progress, 0) / total) : 0

  return {
    total,
    completed,
    onTrack,
    atRisk,
    offTrack,
    averageProgress,
    goals: rows,
  }
}

/**
 * Returns upcoming non-completed elements whose `endDate` falls within `[now, now + windowDays]`,
 * sorted soonest first.
 */
export const upcomingDates = (
  elements: InsightElement[],
  windowDays = 30,
  now: Date | number | string = Date.now(),
): UpcomingDateRow[] => {
  const nowMs = resolveNowMs(now)
  const maxMs = nowMs + windowDays * MS_PER_DAY
  const results: UpcomingDateRow[] = []

  for (const el of elements) {
    if (!el || typeof el !== 'object') continue
    if (el.isCompleted === true || el.boardStatus === 'COMPLETE' || el.workflowStatus === 'COMPLETE') {
      continue
    }
    if (typeof el.progress === 'number' && el.progress >= 100) {
      continue
    }
    const endDateMs = toDueEndMs(el.endDate)
    if (endDateMs == null) continue
    if (endDateMs < nowMs || endDateMs > maxMs) continue

    const daysUntil = Math.max(0, Math.ceil((endDateMs - nowMs) / MS_PER_DAY))
    results.push({
      id: el.id,
      title: el.title,
      type: el.type ?? 'TASK',
      endDateMs,
      daysUntil,
    })
  }

  return results.sort((a, b) => a.endDateMs - b.endDateMs)
}

// ----------------------------------------------------------------------------
// C2. healthOf per OV-P4
// ----------------------------------------------------------------------------

const isGoalProgressRow = (g: InsightGoal | GoalProgressRow): g is GoalProgressRow => {
  return 'isPastDue' in g && 'status' in g
}

/**
 * Plain health rules per OV-P4:
 * - Off track (`off_track`): any goal is past due and <100%, OR overdue assigned items are >15% of open ones.
 * - At risk (`at_risk`): a goal due within 14 days is behind its time-elapsed share by >20 points, OR overdue >5% of open.
 * - Otherwise On track (`on_track`).
 * Returns the state and the worst cause.
 */
export const healthOf = (input: HealthInput): HealthResult => {
  const nowMs = resolveNowMs(input.now)

  const goalRows: GoalProgressRow[] = (() => {
    const rawGoals = input.goals ?? []
    if (rawGoals.length === 0) return []
    if (isGoalProgressRow(rawGoals[0])) {
      return rawGoals as GoalProgressRow[]
    }
    return goalProgress(rawGoals as InsightGoal[], nowMs).goals
  })()

  let openCount = input.openCount ?? 0
  let overdueCount = input.overdueCount ?? 0

  if (input.items && (input.openCount == null || input.overdueCount == null)) {
    let computedOpen = 0
    let computedOverdue = 0
    for (const item of input.items) {
      if (isItemIgnored(item)) continue
      if (!item.assigneeId) continue
      if (!isItemCompleted(item)) {
        computedOpen += 1
        if (isItemOverdue(item, nowMs)) {
          computedOverdue += 1
        }
      }
    }
    if (input.openCount == null) openCount = computedOpen
    if (input.overdueCount == null) overdueCount = computedOverdue
  }

  const overdueShare = openCount > 0 ? overdueCount / openCount : 0

  // 1. Check Off track conditions
  const pastDueGoals = goalRows.filter((g) => g.isPastDue && g.progress < 100)
  if (pastDueGoals.length > 0) {
    const worstGoal = [...pastDueGoals].sort((a, b) => (a.endDateMs ?? 0) - (b.endDateMs ?? 0))[0]
    return {
      state: 'off_track',
      label: 'Off track',
      causeKind: 'goal_past_due',
      worstCause:
        pastDueGoals.length === 1
          ? `Goal "${worstGoal.title}" is past due (${worstGoal.progress}% complete)`
          : `${pastDueGoals.length} goals are past due (oldest: "${worstGoal.title}")`,
    }
  }

  if (openCount > 0 && overdueShare > 0.15) {
    const pct = Math.round(overdueShare * 100)
    return {
      state: 'off_track',
      label: 'Off track',
      causeKind: 'overdue_over_15_pct',
      worstCause: `${overdueCount} of ${openCount} open assigned items (${pct}%) are overdue`,
    }
  }

  // 2. Check At risk conditions
  const behindGoals = goalRows.filter(
    (g) =>
      g.status === 'at_risk' ||
      (g.progress < 100 &&
        g.daysRemaining != null &&
        g.daysRemaining >= 0 &&
        g.daysRemaining <= 14 &&
        g.elapsedShare != null &&
        g.elapsedShare - g.progress > 20),
  )
  if (behindGoals.length > 0) {
    const worstBehind = [...behindGoals].sort(
      (a, b) => (b.elapsedShare ?? 0) - b.progress - ((a.elapsedShare ?? 0) - a.progress),
    )[0]
    return {
      state: 'at_risk',
      label: 'At risk',
      causeKind: 'goal_behind_schedule',
      worstCause: `Goal "${worstBehind.title}" is due soon and behind schedule (${worstBehind.progress}% done)`,
    }
  }

  if (openCount > 0 && overdueShare > 0.05) {
    const pct = Math.round(overdueShare * 100)
    return {
      state: 'at_risk',
      label: 'At risk',
      causeKind: 'overdue_over_5_pct',
      worstCause: `${overdueCount} of ${openCount} open assigned items (${pct}%) are overdue`,
    }
  }

  return {
    state: 'on_track',
    label: 'On track',
    causeKind: 'healthy',
    worstCause: null,
  }
}

// ----------------------------------------------------------------------------
// C4. viewerTier and trimForTier
// ----------------------------------------------------------------------------

/**
 * Maps workspace role + ownership to one of the 3 Overview tiers (OV-P2):
 * - owner, ADMIN, or EDITOR → `'lead'`
 * - VIEWER or READONLY → `'viewer'`
 * - HELPER → `'helper'`
 */
export const viewerTier = (role?: string | null, isOwner = false): ViewerTier => {
  if (isOwner) return 'lead'
  const normalized = (role ?? '').trim().toUpperCase()
  if (normalized === 'OWNER' || normalized === 'ADMIN' || normalized === 'EDITOR') {
    return 'lead'
  }
  if (normalized === 'HELPER') {
    return 'helper'
  }
  if (normalized === 'VIEWER' || normalized === 'READONLY') {
    return 'viewer'
  }
  return 'viewer'
}

/**
 * Filters derived Overview data by viewer tier (OV-P2, OV-P5, OV-P9):
 * - `'lead'`: full workspace data.
 * - `'viewer'`: never receives another person's numbers (`people` only keeps the viewer's own row if present;
 *   `attention` strips `person_overloaded` and `person_quiet` rules).
 * - `'helper'`: receives no activity feed (`activityFeed: null`), no people list (`people: []`),
 *   no modules (`modules: null`), and in `attention` only their own overdue items.
 */
export const trimForTier = <T extends OverviewTierData>(
  data: T,
  tier: ViewerTier,
  uid?: string | null,
): T => {
  const normalizedUid = (uid ?? '').trim()
  if (tier === 'lead') {
    return { ...data }
  }

  if (tier === 'viewer') {
    const ownPersonRows = Array.isArray(data.people)
      ? data.people.filter((p) => normalizedUid && p.uid === normalizedUid)
      : undefined
    const filteredTeamStats = data.teamStats
      ? {
          ...data.teamStats,
          people: data.teamStats.people.filter((p) => normalizedUid && p.uid === normalizedUid),
        }
      : data.teamStats
    const filteredAttention = Array.isArray(data.attention)
      ? data.attention.filter(
          (item) => item.kind !== 'person_overloaded' && item.kind !== 'person_quiet',
        )
      : data.attention

    return {
      ...data,
      ...(ownPersonRows !== undefined ? { people: ownPersonRows } : {}),
      ...(filteredTeamStats !== undefined ? { teamStats: filteredTeamStats } : {}),
      ...(filteredAttention !== undefined ? { attention: filteredAttention } : {}),
    }
  }

  // tier === 'helper'
  const helperAttention = Array.isArray(data.attention)
    ? data.attention.filter(
        (item) =>
          item.kind === 'item_overdue' &&
          Boolean(normalizedUid) &&
          item.assigneeId === normalizedUid,
      )
    : data.attention

  const helperTeamStats = data.teamStats
    ? {
        ...data.teamStats,
        people: [],
      }
    : data.teamStats

  return {
    ...data,
    people: [],
    teamStats: helperTeamStats,
    attention: helperAttention,
    activityFeed: null,
    modules: null,
  }
}

// ----------------------------------------------------------------------------
// C5. canActOn for OV-P6's "N need you"
// ----------------------------------------------------------------------------

/**
 * Determines whether the current viewer can act on an item/attention entry for OV-P6's "N need you":
 * True when the viewer is the assignee OR has edit permission on the item.
 */
export const canActOn = (
  item: {
    id?: string
    targetId?: string
    assigneeId?: string | null
    scopeWrite?: string[]
    assignedEditors?: string[]
    createdById?: string | null
    canAct?: boolean
  } | null | undefined,
  viewer: ViewerContext | null | undefined,
): boolean => {
  if (!item || !viewer) return false
  if (typeof item.canAct === 'boolean') return item.canAct

  const uid = (viewer.uid ?? '').trim()
  const tier = viewer.tier ?? viewerTier(viewer.role, viewer.isOwner)

  // Viewers are read-only across the workspace
  if (tier === 'viewer') {
    return false
  }

  // Direct assignee can always act on their item
  if (uid && item.assigneeId && item.assigneeId === uid) {
    return true
  }

  // Helpers can only act on items assigned to them (or explicitly in scopeWrite)
  if (tier === 'helper') {
    if (uid && Array.isArray(item.scopeWrite) && item.scopeWrite.includes(uid)) {
      return true
    }
    return false
  }

  // Leads (Owner / Admin / Editor): check explicit editableIds or scopeWrite if restricted
  const targetKey = item.targetId ?? item.id
  if (viewer.editableIds) {
    const set =
      viewer.editableIds instanceof Set ? viewer.editableIds : new Set(viewer.editableIds)
    if (targetKey && set.has(targetKey)) return true
    return false
  }

  if (Array.isArray(item.scopeWrite) && item.scopeWrite.length > 0) {
    return Boolean(uid && item.scopeWrite.includes(uid))
  }

  if (Array.isArray(item.assignedEditors) && item.assignedEditors.length > 0) {
    if (uid && item.assignedEditors.includes(uid)) return true
  }

  // Unrestricted lead (Owner / Admin / Editor) can edit workspace items
  return tier === 'lead'
}

// ----------------------------------------------------------------------------
// C3. attentionItems per OV-P5
// ----------------------------------------------------------------------------

const resolveBudgetOverrun = (
  budget: BudgetPaceInput | null | undefined,
  nowMs: number,
): { usedPct: number; elapsedPct: number; deltaPoints: number } | null => {
  if (!budget) return null

  let usedPct: number | null = null
  if (typeof budget.usedShare === 'number' && Number.isFinite(budget.usedShare)) {
    usedPct = budget.usedShare <= 1 && budget.usedShare >= 0 && (budget.elapsedTimeShare ?? 0) <= 1
      ? budget.usedShare * 100
      : budget.usedShare
  } else if (
    typeof budget.usedMinor === 'number' &&
    typeof budget.limitMinor === 'number' &&
    budget.limitMinor > 0
  ) {
    usedPct = (budget.usedMinor / budget.limitMinor) * 100
  }
  if (usedPct == null || !Number.isFinite(usedPct)) return null

  let elapsedPct: number | null = null
  if (typeof budget.elapsedTimeShare === 'number' && Number.isFinite(budget.elapsedTimeShare)) {
    elapsedPct =
      budget.elapsedTimeShare <= 1 && budget.elapsedTimeShare >= 0 && (budget.usedShare ?? 100) <= 1
        ? budget.elapsedTimeShare * 100
        : budget.elapsedTimeShare
  } else {
    const startMs = toTimestampMs(budget.startDate)
    const endMs = toDueEndMs(budget.endDate)
    if (startMs != null && endMs != null && endMs > startMs) {
      elapsedPct = Math.max(0, Math.min(100, ((nowMs - startMs) / (endMs - startMs)) * 100))
    }
  }
  if (elapsedPct == null || !Number.isFinite(elapsedPct)) return null

  const deltaPoints = usedPct - elapsedPct
  if (deltaPoints > 15) {
    return {
      usedPct: Math.round(usedPct),
      elapsedPct: Math.round(elapsedPct),
      deltaPoints: Math.round(deltaPoints),
    }
  }
  return null
}

/**
 * Plain attention rules per OV-P5 (max 6 items, sorted by severity: `bad` before `warn`):
 * - Overdue assigned items (oldest due first; for helpers, only their own overdue items)
 * - Goals Off track (past due and <100%; leads & viewers)
 * - People with >2x the team median open items, minimum 6 → Overloaded (leads only)
 * - People with open items and no activity for 5+ days → Quiet (leads only)
 * - Stock items at or below reorder level, including per-location `reorderPointByLocation` (leads & viewers)
 * - Budget used share > elapsed time share + 15 points (leads & viewers)
 */
export const attentionItems = (input: AttentionInput): AttentionItem[] => {
  const nowMs = resolveNowMs(input.now)
  const tier = input.viewerTier ?? (input.viewerRole != null || input.isOwner != null ? viewerTier(input.viewerRole, input.isOwner) : 'lead')
  const viewerUid = (input.viewerUid ?? '').trim()
  const rawItems = input.items ?? []
  const rawMembers = input.members ?? []
  const rawGoals = input.goals ?? []
  const rawStock = input.stockItems ?? []

  const candidates: Array<{ item: AttentionItem; sortOrder: number; secondarySort: number }> = []

  // 1. Overdue assigned items (oldest due first)
  const overdueItems = rawItems
    .filter((item) => {
      if (isItemIgnored(item)) return false
      if (!item.assigneeId) return false
      if (tier === 'helper' && item.assigneeId !== viewerUid) return false
      return isItemOverdue(item, nowMs)
    })
    .map((item) => ({
      item,
      dueMs: getItemDueMs(item) ?? nowMs,
    }))
    .sort((a, b) => a.dueMs - b.dueMs)

  for (const { item, dueMs } of overdueItems) {
    const daysLate = Math.max(1, Math.floor((nowMs - dueMs) / MS_PER_DAY))
    const label = (item.title ?? item.text ?? 'Untitled item').trim()
    const entry: AttentionItem = {
      id: `attn-overdue-${item.id}`,
      kind: 'item_overdue',
      severity: 'bad',
      title: label,
      detail: `${daysLate}d overdue`,
      targetId: item.id,
      assigneeId: item.assigneeId ?? null,
      scopeWrite: item.scopeWrite,
      assignedEditors: item.assignedEditors,
      createdById: item.createdById ?? null,
    }
    entry.canAct = canActOn(entry, { uid: viewerUid, tier })
    candidates.push({
      item: entry,
      sortOrder: 10,
      secondarySort: dueMs, // oldest (smallest timestamp) first
    })
  }

  // Helpers ONLY see their own overdue items
  if (tier === 'helper') {
    return candidates.slice(0, 6).map((c) => c.item)
  }

  // 2. Goals Off track (past due and < 100%)
  const goalSummary = goalProgress(rawGoals, nowMs)
  for (const g of goalSummary.goals) {
    if (g.status === 'off_track') {
      const entry: AttentionItem = {
        id: `attn-goal-${g.id}`,
        kind: 'goal_off_track',
        severity: 'bad',
        title: g.title,
        detail: `Off track (${g.progress}% done, past due)`,
        targetId: g.id,
      }
      entry.canAct = canActOn(entry, { uid: viewerUid, tier })
      candidates.push({
        item: entry,
        sortOrder: 20,
        secondarySort: g.endDateMs ?? 0,
      })
    }
  }

  // 3. People rules (ONLY for leads per OV-P5)
  if (tier === 'lead') {
    const tStats = teamStats(rawItems, rawMembers, nowMs)
    for (const person of tStats.people) {
      if (person.flag === 'overloaded') {
        const entry: AttentionItem = {
          id: `attn-overloaded-${person.uid}`,
          kind: 'person_overloaded',
          severity: 'warn',
          title: `${person.displayName} is overloaded`,
          detail: `${person.open} open items (team median ${tStats.medianOpen})`,
          targetId: person.uid,
          assigneeId: person.uid,
        }
        entry.canAct = true
        candidates.push({
          item: entry,
          sortOrder: 30,
          secondarySort: -person.open,
        })
      } else if (person.flag === 'quiet') {
        const quietDays =
          person.lastActiveAt != null ? Math.floor((nowMs - person.lastActiveAt) / MS_PER_DAY) : 5
        const entry: AttentionItem = {
          id: `attn-quiet-${person.uid}`,
          kind: 'person_quiet',
          severity: 'warn',
          title: `${person.displayName} has been quiet`,
          detail: `${person.open} open items, no activity for ${quietDays}d`,
          targetId: person.uid,
          assigneeId: person.uid,
        }
        entry.canAct = true
        candidates.push({
          item: entry,
          sortOrder: 40,
          secondarySort: -quietDays,
        })
      }
    }
  }

  // 4. Low stock items (global threshold or per-location reorderPointByLocation)
  for (let i = 0; i < rawStock.length; i++) {
    const s = rawStock[i]
    if (!isStockItemLow(s)) continue
    const { threshold, shortfall } = getStockShortfall(s)
    const stockId = (s as { id?: string }).id ?? `stock-${i}`
    const stockName = (s as { name?: string }).name ?? `Stock item ${i + 1}`
    const entry: AttentionItem = {
      id: `attn-stock-${stockId}`,
      kind: 'stock_low',
      severity: s.quantity <= 0 ? 'bad' : 'warn',
      title: `${stockName} is low on stock`,
      detail: `${s.quantity} on hand (reorder at ${threshold})`,
      targetId: stockId,
    }
    entry.canAct = canActOn(entry, { uid: viewerUid, tier })
    candidates.push({
      item: entry,
      sortOrder: s.quantity <= 0 ? 25 : 50,
      secondarySort: -shortfall,
    })
  }

  // 5. Budget used share > elapsed time share + 15 points
  const budgetOverrun = resolveBudgetOverrun(input.budget, nowMs)
  if (budgetOverrun) {
    const entry: AttentionItem = {
      id: 'attn-budget-pace',
      kind: 'budget_pace',
      severity: budgetOverrun.usedPct >= 100 ? 'bad' : 'warn',
      title: 'Budget spend ahead of schedule',
      detail: `${budgetOverrun.usedPct}% used vs ${budgetOverrun.elapsedPct}% time elapsed (+${budgetOverrun.deltaPoints} pts)`,
      targetId: 'budget',
    }
    entry.canAct = canActOn(entry, { uid: viewerUid, tier })
    candidates.push({
      item: entry,
      sortOrder: budgetOverrun.usedPct >= 100 ? 28 : 60,
      secondarySort: -budgetOverrun.deltaPoints,
    })
  }

  // Sort by severity ('bad' before 'warn'), then sortOrder, then secondarySort
  candidates.sort((a, b) => {
    const sevA = a.item.severity === 'bad' ? 0 : 1
    const sevB = b.item.severity === 'bad' ? 0 : 1
    if (sevA !== sevB) return sevA - sevB
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    return a.secondarySort - b.secondarySort
  })

  return candidates.slice(0, 6).map((c) => c.item)
}
